import { z, ZodError } from "zod";

// We've defined here the validations & schema for making sure the env vars ARE correct.
const defaultValidation = z.string().min(1, "Env Var is not defined");
const envSchema = z.object({
  VITE_APP_ENV: defaultValidation,
  VITE_APP_NAME: defaultValidation,
  VITE_API_URL: defaultValidation,
  VITE_SENTRY_DSN_PUBLIC: defaultValidation,
  VITE_SENTRY_ENVIRONMENT: defaultValidation,
  VITE_STRIPE_PUBLIC_KEY: defaultValidation,
  VITE_INTERCOM_APP_ID: defaultValidation,
  VITE_COACHING_HUB_FILE_URL: defaultValidation,
  VITE_CUSTOMER_IO_WRITE_KEY: defaultValidation,
  VITE_QUORA_PIXEL_ID: defaultValidation,
  VITE_APPLE_PAY_ENABLED: z.stringbool().catch(false),
  VITE_KLARNA_ENABLED: z.stringbool().catch(false),
  VITE_REFERRAL_ENABLED: z.stringbool().catch(false),
  VITE_AMAZON_CONNECT_CHAT_SNIPPET_ID: defaultValidation,
  VITE_AMAZON_CONNECT_CHAT_SOURCE: defaultValidation,
  VITE_AMAZON_CONNECT_CHAT_SESSION_ID: defaultValidation,
  VITE_MOUSEFLOW_SITE_ID: z.string().catch(""),
  VITE_DISCOUNT_PER_REFERRAL_CENTS: z.number().catch(0),
  VITE_REFERRAL_MAX_CREDITS_CENTS: z.number().catch(0),
  VITE_REFERRAL_MIN_CREDITS_CENTS: z.number().catch(0),
  VITE_GOOGLE_MAPS_API_KEY: defaultValidation,
  VITE_TRUSTPILOT_REVIEWS_COUNT: z.string().catch("8"),
});

type EnvValues = z.infer<typeof envSchema>;

// IDEA: trigger a toast message on dev instead of just a console error
function logEnvError(errors: ZodError<EnvValues>) {
  const { _errors, ...formattedErrors } = errors.format();

  console.error(
    `\nENVIRONMENT VARIABLES ERRORS:\n-----\n${Object.entries(formattedErrors)
      .map(([name, { _errors }]) => `"${name}": ${_errors.join(", ")}`)
      .join("\n")}\n-----\n`,
  );
}

function checkEnv() {
  try {
    return envSchema.parse(import.meta.env);
  } catch (errors) {
    if (errors instanceof ZodError) {
      logEnvError(errors as ZodError<EnvValues>);
    }

    // So! We had some validation errors
    // let's just make sure the shape of the object is what we are expecting
    return Object.fromEntries(
      Object.keys(envSchema.shape).map((key) => [
        key,
        import.meta.env[key] ?? "",
      ]),
    ) as EnvValues;
  }
}

export const env = checkEnv();

// Instead of using `APP_ENV === "production"`, we use the VITE_API_URL because in stage, APP_ENV is set as "production".
export const IS_PRODUCTION =
  env.VITE_API_URL === "https://app.levelsrx.com/api";
